import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';
import { AUTHORS } from 'constants/articles/authors';
import { Button } from 'components/Buttons';
import Box from '@mui/material/Box/Box';

import HeaderImage from 'assets/resources/using-synthetic-data-from-AILiveSim/HeaderImage.webp';

const commonData = {
  ...AUTHORS['Tristan Klempka'],
  title: 'Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models',
  publishDate: 'November 28, 2025',
  minutesOfRead: 20,
};

export const usingSyntheticDataFromAILiveSimMeta: PageMeta = {
  url: '/whitepapers/using-synthetic-data-from-ailivesim',
  title: commonData.title,
  description: 'Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models',
};

export const usingSyntheticDataFromAILiveSimData: ArticleSections[] = [
  {
    resourceType: 'White Paper',
    text: (
      <>
        <p>
          Building reliable computer-vision systems for maritime environments remains a difficult
          challenge. Real data is expensive and time-consuming to collect, and even when available
          it often fails to cover the wide range of rare or hazardous scenarios that autonomous
          systems must handle. In contrast, synthetic data can be generated at scale, designed to
          expose models to difficult edge cases, and systematically varied to explore environmental
          conditions that are nearly impossible to capture in the real world.
        </p>
        <p>
          This white paper outlines how we leveraged AILiveSim simulation solution to generate
          synthetic maritime data and how it can be used to significantly improve object-detection
          performance. Our experiments demonstrate that synthetic data augmentation can achieve
          F1@50 of 0.98 and mAP@50 of 0.99 on real test footage, even when training with limited
          real-world data.
        </p>
        <p>
          <strong>
            <a
              href="https://share.hsforms.com/ui-forms-embed-components-app.html?_hsFormId=fce8089c-7651-4736-910f-ec7bd45a91d4&_hsPortalId=7805184&_hsHublet=na1"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#348EE9', textDecoration: 'underline' }}
            >
              Download the full White Paper
            </a>
          </strong>{' '}
          to explore detailed methodology, experimental results, and comprehensive analysis of how
          synthetic data from AILiveSim enhances maritime object-detection model performance.
        </p>
      </>
    ),
    image: HeaderImage,
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    ...commonData,
  },

  {
    type: 'common',
    title: 'Summary',
    text: (
      <>
        <p>
          This white paper presents a comprehensive study on using AILiveSim synthetic data to train
          robust maritime object-detection models. The research demonstrates that synthetic data
          generation can significantly improve model performance when real-world maritime data is
          limited or expensive to collect.
        </p>
        <p>
          <strong>Key Findings:</strong>
        </p>
        <ul>
          <li>
            Synthetic-augmented training with 2,500 real images and 5,000 synthetic samples achieved
            F1@50 of 0.98 and mAP@50 of 0.99 on real test data
          </li>
          <li>
            Fully synthetic training from scratch (7,500 synthetic images) achieved competitive
            performance with F1@50 of 0.93 and mAP@50 of 0.96
          </li>
          <li>
            AILiveSim&apos;s scenario-based workflow enabled generation of 12,000 annotated frames
            with domain randomization across 24 maritime scenario variations
          </li>
          <li>
            Synthetic data effectively acts as a structural regularizer, preventing model
            over-specialization and improving generalization to real-world conditions
          </li>
        </ul>
        <p>
          The study highlights how synthetic data can capture rare or hazardous maritime scenarios
          that are difficult or impossible to capture in real-world conditions, making AILiveSim an
          effective solution for improving model performance when real maritime data is limited or
          unavailable.
        </p>

        <p
          style={{
            fontSize: '24px',
            lineHeight: '150%',
            marginBottom: '16px',
            fontWeight: '700',
            marginTop: '48px',
          }}
        >
          Want to know more?
        </p>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '48px',
          }}
        >
          <Button
            btnType="secondary"
            style={{ color: '#348EE9', borderColor: '#348EE9', height: '40px' }}
            onClick={() => {
              const win = window as typeof window & {
                openDownloadWhitePaperModal?: () => void;
              };
              if (win.openDownloadWhitePaperModal) {
                win.openDownloadWhitePaperModal();
              }
            }}
          >
            Access the Research Dataset
          </Button>
        </Box>

        <p
          style={{ fontSize: '24px', lineHeight: '150%', marginBottom: '16px', fontWeight: '700' }}
        >
          Want to go further? Interested in Producing Your Own Synthetic Dataset with AILiveSim?
        </p>
        <Box
          sx={{
            marginTop: '24px',
            display: 'flex',
            flexDirection: { xxs: 'column', sm: 'row' },
            gap: '16px',
            alignItems: { xxs: 'stretch', sm: 'flex-start' },
            marginBottom: '48px',
          }}
        >
          <Button
            btnType="primary"
            onClick={() => {
              window.open(
                'https://meetings.hubspot.com/mharalson?uuid=597f70b5-efa6-4cd4-89ef-af25c6dad16f',
                '_blank',
                'noopener,noreferrer',
              );
            }}
          >
            Book a Meeting
          </Button>
          <Button
            btnType="secondary"
            style={{ borderColor: '#348EE9', color: '#348EE9' }}
            onClick={() => {
              const win = window as typeof window & {
                openAskQuestionModal?: () => void;
              };
              if (win.openAskQuestionModal) {
                win.openAskQuestionModal();
              }
            }}
          >
            Questions about the research?
          </Button>
        </Box>
      </>
    ),
  },
];

export const usingSyntheticDataFromAILiveSimCard: TCardProps = {
  description:
    'This describes how AILiveSim synthetic data trains robust maritime object-detection models when real sea data is expensive or unavailable. Covers generating 12,000 annotated frames with domain randomization, achieving F1@50 of 0.93 and mAP@50 of 0.96 on real test footage. Synthetic scenarios safely expose models to rare maritime conditions impossible to capture at sea.',
  image: HeaderImage,
  type: 'White Papers',
  category: 'whitepapers',
  postUrl: usingSyntheticDataFromAILiveSimMeta.url,
  ...commonData,
  hashTags: [EHashtag.AutonomousVehicles, EHashtag.SyntheticData, EHashtag.AISimulation],
};
